import { Box, Paper, Tab, Tabs, Typography } from "@material-ui/core";
import { useMemo, useEffect, useState, useCallback } from "react";
import { LoadWrapper, ServiceListItemsEditor } from "..";
import { useServiceListItems } from "../../../graphql/hooks";
import {
  Service,
  ServiceTypeListItem,
  ServiceTypeListItemType,
} from "../../../graphql/models";
import { ApartmentsTabs } from "../apartments_tabs";
import { BuildingsTabs } from "../buildings_tabs";

import useServiceListItemsEditorTabsStyle from "./service_list_items_editor_tabs.style";

enum ServiceTypeListItemsTabs {
  PRODUCTS = 1,
  SERVICES = 0,
  APARTMENTS = 2,
}

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={3}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

type ApartmentServiceListItemsWrapperProps = {
  service: Service;
  apartmentId: string;
  ItemsRendererComponent: React.FC<{
    items: ServiceTypeListItem[];
    refetch: () => void;
  }>;
};
function ApartmentServiceListItemsWrapper(
  props: ApartmentServiceListItemsWrapperProps
) {
  const { apartmentId, service, ItemsRendererComponent } = props;

  const { fetch, fetchServiceListItemsLoading, fetchServiceListItemsData } =
    useServiceListItems();

  const fetchForApartment = useCallback(() => {
    return fetch({
      variables: {
        apartmentId,
        serviceId: service.id,
      },
    });
  }, [apartmentId, fetch, service.id]);

  const currentItems: ServiceTypeListItem[] = useMemo(() => {
    return fetchServiceListItemsData?.serviceTypeListItems ?? [];
  }, [fetchServiceListItemsData]);

  useEffect(() => {
    fetchForApartment();
  }, [fetchForApartment]);

  return (
    <LoadWrapper loading={fetchServiceListItemsLoading}>
      <ItemsRendererComponent
        items={currentItems}
        refetch={fetchForApartment}
      />
    </LoadWrapper>
  );
}

export interface ServiceListItemsEditorTabsProps {
  askProducts: boolean;
  askCategories?: boolean;
  askRequiredTime?: boolean;
  service: Service;
  isComposable: boolean;
  suggestedCategories: {
    it: string[];
    en: string[];
  };
  readonly?: boolean;
}

export function ServiceListItemsEditorTabs(
  props: ServiceListItemsEditorTabsProps
) {
  const {
    askProducts,
    askCategories,
    askRequiredTime,
    service,
    isComposable,
    suggestedCategories,
    readonly = false,
  } = props;
  const classes = useServiceListItemsEditorTabsStyle();

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  const { fetch, fetchServiceListItemsLoading, fetchServiceListItemsData } =
    useServiceListItems();

  const fetchItems = useCallback(() => {
    switch (selectedTabIndex) {
      case ServiceTypeListItemsTabs.PRODUCTS:
        fetch({
          variables: {
            serviceId: service.id,
            type: ServiceTypeListItemType.PRODUCT,
          },
        });
        break;
      case ServiceTypeListItemsTabs.SERVICES:
        fetch({
          variables: {
            serviceId: service.id,
            type: ServiceTypeListItemType.SERVICE,
          },
        });
        break;
      case ServiceTypeListItemsTabs.APARTMENTS:
        break;
      default:
        break;
    }
  }, [selectedTabIndex, service, fetch]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const refetchCurrentItems = () => fetchItems();

  const currentItems: ServiceTypeListItem[] = useMemo(() => {
    return fetchServiceListItemsData?.serviceTypeListItems ?? [];
  }, [fetchServiceListItemsData]);

  return (
    <Paper className={classes.root}>
      <Tabs
        value={selectedTabIndex}
        onChange={(e, idx) => setSelectedTabIndex(idx)}
        indicatorColor="primary"
        textColor="primary"
        centered
      >
        <Tab label="Servizi" />
        <Tab disabled={!askProducts} label="Prodotti" />
        <Tab label="Servizi esclusivi per singoli appartamenti" />
      </Tabs>
      <TabPanel
        className={classes.tabPanel}
        value={selectedTabIndex}
        index={ServiceTypeListItemsTabs.SERVICES}
      >
        <LoadWrapper loading={fetchServiceListItemsLoading}>
          <ServiceListItemsEditor
            key={`${service.id}-list-item-services`}
            items={currentItems}
            onItemDeleted={refetchCurrentItems}
            onItemUpdated={refetchCurrentItems}
            service={service}
            isComposable={isComposable}
            askCategories={askCategories}
            suggestedCategories={suggestedCategories}
            type={ServiceTypeListItemType.SERVICE}
            askRequiredTime={askRequiredTime}
            readonly={readonly}
          />
        </LoadWrapper>
      </TabPanel>
      <TabPanel
        className={classes.tabPanel}
        value={selectedTabIndex}
        index={ServiceTypeListItemsTabs.PRODUCTS}
      >
        <LoadWrapper loading={fetchServiceListItemsLoading}>
          <ServiceListItemsEditor
            key={`${service.id}-list-item-products`}
            items={currentItems}
            onItemDeleted={refetchCurrentItems}
            onItemUpdated={refetchCurrentItems}
            service={service}
            isComposable={isComposable}
            askCategories={false}
            suggestedCategories={suggestedCategories}
            type={ServiceTypeListItemType.PRODUCT}
            readonly={readonly}
            askRequiredTime={false}
          />
        </LoadWrapper>
      </TabPanel>
      <TabPanel
        className={classes.tabPanel}
        value={selectedTabIndex}
        index={ServiceTypeListItemsTabs.APARTMENTS}
      >
        <LoadWrapper loading={fetchServiceListItemsLoading}>
          <BuildingsTabs
            indicatorColor="secondary"
            textColor="secondary"
            TabContentComponent={({ building }) => (
              <ApartmentsTabs
                indicatorColor="secondary"
                textColor="secondary"
                variant="scrollable"
                buildingId={building.id}
                TabContentComponent={({ apartment }) => (
                  <Box p={1}>
                    <ApartmentServiceListItemsWrapper
                      service={service}
                      apartmentId={apartment.id}
                      ItemsRendererComponent={({ items, refetch }) => (
                        <ServiceListItemsEditor
                          targetApartment={apartment}
                          key={`${service.id}-${apartment.id}-list-item-products`}
                          items={items}
                          onItemDeleted={refetch}
                          onItemUpdated={refetch}
                          service={service}
                          isComposable={isComposable}
                          askCategories={false}
                          suggestedCategories={suggestedCategories}
                          type={0}
                          readonly={readonly}
                          askRequiredTime={true}
                        />
                      )}
                    />
                  </Box>
                )}
              />
            )}
          />
        </LoadWrapper>
      </TabPanel>
    </Paper>
  );
}
